import React from 'react';
import Table from '../Table/Table';
import VerticalContent from '../VerticalContent/VerticalContent';
import EnhancedMarkdown from '../EnhancedMarkdown/EnhancedMarkdown';
import {
  IconDefaultsContextProvider,
  iconSizeExtraLarge,
} from '../../contexts/useIconDefaults';
import type { BuildsTableColumnViewModel } from '../../typings/viewModels/buildsTableColumnViewModel';
import BuildsNoDataPlaceholder from '../BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import useTableHiddenColumns from '@resources/js/contexts/useTableHiddenColumns';
import cx from 'classnames';
import WarningParagraph from '@resources/js/components/WarningParagraph/WarningParagraph';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';

export type BuildsGearTablesViewModel = {
  title: string;
  description: string;
  warning: string | null;
  tables: Array<{
    columns: Record<'item' | 'popularity', BuildsTableColumnViewModel> &
      Partial<Record<'dps' | 'hps' | 'maxKey', BuildsTableColumnViewModel>>;
    data: Array<{
      item: string;
      maxKey?: string;
      dps?: string;
      hps?: string;
      popularity: string;
    }>;
  }>;
};

export default function BuildsGearTablesSection({
  title,
  description,
  warning,
  tables,
}: BuildsGearTablesViewModel): JSX.Element {
  const { hiddenColumns } = useTableHiddenColumns();
  const numberOfVisibleColumns =
    tables.length === 0
      ? 0
      : Object.keys(tables[0].columns).filter(
          (column) => !hiddenColumns.includes(column)
        ).length;
  const isWide = numberOfVisibleColumns > 3;

  return (
    <VerticalContent
      className={cx({
        'builds-gear-tables-section': true,
        'builds-gear-tables-section--wide': isWide,
      })}
      gap={'large'}
    >
      <EnhancedMarkdown
        markdown={`
# ${title}

${description}
`}
        overrides={buildsEnhancedMarkdownOverrides}
      />
      {warning && (
        <WarningParagraph>
          <EnhancedMarkdown markdown={warning} wrapped={false} />
        </WarningParagraph>
      )}
      <IconDefaultsContextProvider
        overrides={{
          icon: {
            size: iconSizeExtraLarge,
            rounded: false,
            bordered: true,
          },
          item: { size: 28 },
        }}
      >
        {tables.length > 0 ? (
          <div className='builds-gear-tables-section__group'>
            {tables.map((table, index) => {
              return (
                <Table
                  key={index}
                  {...{
                    ...table,
                    columns: {
                      ...table.columns,
                      item: {
                        ...table.columns.item,
                        maxWidth: 220,
                      },
                    },
                  }}
                  enhancedMarkdownEnabled
                  style='v2-bordered'
                  truncate={{
                    limit: 3,
                    showMoreButton: { styles: ['link', 'bold'] },
                  }}
                />
              );
            })}
          </div>
        ) : (
          <BuildsNoDataPlaceholder />
        )}
      </IconDefaultsContextProvider>
    </VerticalContent>
  );
}
